import CardItemDTO from '@src/adapters/dto/card/CardItemDTO';
import { useEffect, useState } from 'react';
import useCachedCategoriesList from '@src/adapters/hooks/category/useCachedCategoriesList';
import { Box, Button, Typography } from '@mui/material';
import Category from './components/Category';
import CategoryHeaderDTO from '@src/adapters/dto/category/CategoryHeaderDTO';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CardDTO from '@src/adapters/dto/card/CardDTO';
import InventorySelection from './components/InventorySelection';
import { CardModificationDTO } from '@src/adapters/dto/card/CardModificationDTO';

interface Props {
  card: CardDTO;
  isEdit: boolean;
  onItemDeleted(id: string, modification: CardModificationDTO): void;
  onItemUpdated(cardItem: CardItemDTO, modification: CardModificationDTO): void;
  onItemAdded(card: CardItemDTO, modification: CardModificationDTO): void;
  onViewClick(): void;
}

function ContentsTab({
  card,
  isEdit,
  onItemDeleted,
  onItemUpdated,
  onItemAdded,
  onViewClick,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [categories, setCategories] = useState<CategoryHeaderDTO[]>([]);
  const { getList } = useCachedCategoriesList();

  useEffect(() => {
    getList().then((result) => {
      const categoryIds = new Set(card.items.map((item) => item.category.id));
      setCategories(
        result
          .filter((c) => categoryIds.has(c.id))
          .sort((a, b) => a.order - b.order)
      );
    });
  }, [getList, card]);

  const handleCardItemsToggle = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <>
      {card.modifications.length > 0 && (
        <Box
          sx={(theme) => ({
            height: '48px',
            backgroundColor: '#FFF8EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          })}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
            }}>
            <WarningAmberIcon
              sx={(theme) => ({
                color: '#FFAB00',
                marginLeft: theme.spacing(2),
                marginRight: theme.spacing(2),
              })}
            />
            <Typography variant="body2"> This card has changes</Typography>
          </Box>
          <Box
            onClick={onViewClick}
            sx={(theme) => ({
              cursor: 'pointer',
              marginRight: theme.spacing(2),
            })}>
            View
          </Box>
        </Box>
      )}
      {isEdit && (
        <InventorySelection cardId={card.id} onItemAdded={onItemAdded} />
      )}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'rgba(70, 110, 229, 0.05)',
          height: '62px',
          border: '1px solid #E6E7E9',
        }}>
        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '200px',
            padding: '8px',
            paddingLeft: theme.spacing(2),
          })}>
          <Typography variant="h6">Categories</Typography>
        </Box>
        <Box sx={{ display: 'flex' }}>
          <Box
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'right',
              alignItems: 'right',
              width: '100%',
              padding: '8px',
            })}>
            <Button
              color="primary"
              variant="outlined"
              size="small"
              sx={{ height: '30px' }}
              onClick={handleCardItemsToggle}>
              {expanded ? 'Expand All' : 'Collapse all'}
            </Button>
          </Box>
        </Box>
      </Box>
      {categories.map((c) => (
        <Category
          cardId={card.id}
          items={card.items.filter((i) => i.category.id === c.id)}
          category={c}
          key={c.id}
          collapsed={expanded}
          onItemDeleted={onItemDeleted}
          onItemUpdated={onItemUpdated}
          isEdit={isEdit}
        />
      ))}
    </>
  );
}

export default ContentsTab;
